import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resendValidation, validatePhone } from '../../api';
import { handleGlobalApiCalls, syncUser } from '../../helpers/asyncActions';
import { useGlobalContext } from '../../stores/global';
import Button from '../core/Button';
import Modal from './Modal';
import styles from './VerifyEmailPhone.module.scss';

export interface Props {
  onClose: () => void;
}

export default function VerifyEmail({ onClose }: Props) {
  const { user, dispatch, isLoading } = useGlobalContext();
  const { handleSubmit, register } = useForm();
  const userPhone = user?.phone || '';
  const userEmail = user?.email || '';
  const [step, setStep] = useState<
    'open' | 'emailSent' | 'noPhone' | 'enterPhoneCode' | 'phoneVerified'
  >('open');

  const [timer, setTimer] = useState(60);
  const [intervalState, setIntervalState] = useState<any>(null);

  const handleResend = (name: 'email' | 'phone') => {
    handleGlobalApiCalls(dispatch, isLoading, null, () =>
      resendValidation(name).then(() => {
        if (name === 'phone') return setStep('enterPhoneCode');
        setStep('emailSent');
        startTimer();
      })
    );
  };

  const handleVerifyPhoneSubmit = (data: any) => {
    handleGlobalApiCalls(dispatch, isLoading, null, () =>
      validatePhone(data.code)
        .then(() => syncUser(dispatch, true))
        .then(() => setStep('phoneVerified'))
    );
  };

  //resend Email
  const resendEmail = () => {
    if (timer === 0) {
      setTimer(60);
      handleResend('email');
    }
  };

  //Start timer
  const startTimer = () => {
    setIntervalState(
      setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000)
    );
  };

  useEffect(() => {
    if (timer === 0) {
      clearInterval(intervalState);
    } else {
      if (timer < 0) {
        setTimer(0);
      }
    }
    return () => {};
  }, [timer]);

  const steps = {
    open: {
      title: 'Verificar cuenta',
      content: (
        <p>Verifica tu cuenta por medio de tu correo electrónico o celular</p>
      ),
      footer: (
        <div className={styles.footer}>
          <Button
            label="VERIFICAR CORREO"
            onClick={() => handleResend('email')}
          />
          <Button label="CERRAR" onClick={onClose} />
        </div>
      ),
    },
    emailSent: {
      title: 'Mensaje enviado',
      content: (
        <p>
          Te hemos enviado un mensaje de verificación a tu correo electrónico
          <b> {userEmail}</b> para que lo verifiques.
        </p>
      ),
      footer: (
        <div className={styles.footer}>
          <Button
            label={timer === 0 ? 'REENVIAR ' : 'REENVIAR EN ' + timer}
            onClick={() => resendEmail()}
          />
          <Button label="CERRAR" onClick={onClose} />
        </div>
      ),
    },
    noPhone: {
      title: 'Agrega un celular',
      content: (
        <p>
          No tienes celular registrado, ve a <b>Ajustes</b> y agrégalo primero
        </p>
      ),
      footer: (
        <div className={styles.footer}>
          <Button label="CERRAR" onClick={onClose} />
        </div>
      ),
    },
    enterPhoneCode: {
      title: 'Mensaje enviado',
      content: (
        <>
          <p>
            Te hemos enviado un código de verificación a tu celular
            <b> {userPhone}</b> para que lo verifiques. Ingrésalo aqui:
          </p>
          <form
            style={{ textAlign: 'center' }}
            onSubmit={handleSubmit(handleVerifyPhoneSubmit)}
          >
            <input
              className={styles.codeInput}
              type="number"
              placeholder="Código"
              ref={register({ required: true })}
              name="code"
            />
            <Button label="VERIFICAR" type="submit" />
          </form>
        </>
      ),
      footer: (
        <div className={styles.footer}>
          <Button label="REENVIAR" onClick={() => handleResend('phone')} />
        </div>
      ),
    },
    phoneVerified: {
      title: 'Verificación exitosa',
      content: <p>Tu teléfono ha sido verificado exitosamente!</p>,
      footer: (
        <div className={styles.footer}>
          <Button label="CERRAR" onClick={onClose} />
        </div>
      ),
    },
  };

  return (
    <Modal open title={steps[step].title} footer={steps[step].footer}>
      {steps[step].content}
    </Modal>
  );
}
